/**
 * 応答クリップ音声の Web Audio 再生。
 *
 * iOS Safari は <video> の音あり再生（unmute）を自動再生ポリシーで間欠的にブロックする
 * （対照実験で確定：muted 固定=フリーズ0 / unmute=t=0 固着が再現）。
 * そこで映像は muted のまま再生し、音声は「マイクタップで解錠・resume 済みの AudioContext」から
 * Web Audio (AudioBufferSourceNode) で再生する。Web Audio は解錠済みコンテキストからの出力なので
 * iOS でも audible で、unmute 由来の t=0 固着を構造的に回避できる。
 */
import { getKeepAliveAudioContext } from './audioSessionKeepAlive';

let currentSource: AudioBufferSourceNode | null = null;

/**
 * mp4/mp3 等のエンコード済み音声バイナリをデコードして即再生する。
 * @returns 再生開始できたら true
 */
export async function playClipAudio(buffer: ArrayBuffer): Promise<boolean> {
  const ctx = getKeepAliveAudioContext();
  if (!ctx) return false;

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      // resume 失敗時もデコード/再生は試みる
    }
  }

  try {
    // decodeAudioData は渡した ArrayBuffer を detach するため、コピーを渡す。
    const audioBuffer = await ctx.decodeAudioData(buffer.slice(0));
    stopClipAudio();
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);
    source.onended = () => {
      if (currentSource === source) currentSource = null;
    };
    source.start(0);
    currentSource = source;
    return true;
  } catch (error) {
    console.warn('[clipAudioPlayback] decode/play failed:', error);
    return false;
  }
}

/** 再生中の音声を停止する（クリップ終了・中断・切断時）。 */
export function stopClipAudio(): void {
  if (!currentSource) return;
  try {
    currentSource.onended = null;
    currentSource.stop();
  } catch {
    // already stopped
  }
  try {
    currentSource.disconnect();
  } catch {
    // ignore
  }
  currentSource = null;
}
